<template>
  <section class="dp-layout tr-page ps-page">
    <section class="dp-right ps-shell">
    <!-- 页头 -->
    <header class="ps-hero">
      <div class="ps-hero__text">
        <p class="ps-eyebrow">集体备课 · 学校监管</p>
        <h1 class="ps-title">备课监管看板</h1>
        <p class="ps-sub">看清流程卡点，优先处理阻塞队列；点击漏斗可弹窗查看文档。</p>
      </div>
      <div class="ps-hero__meta">
        <div class="ps-school">
          <i class="el-icon-office-building"></i>
          <div>
            <span class="ps-school__k">当前学校</span>
            <strong class="ps-school__v">{{ schoolName || '未选择' }}</strong>
          </div>
        </div>
        <div class="ps-stat-mini">
          <span>阻塞 <b>{{ blocked.length }}</b></span>
          <span>异常 <b>{{ exceptions.length }}</b></span>
        </div>
      </div>
    </header>

    <div class="toolbar-filter-bar ps-filter">
      <el-form :inline="true" class="toolbar-filter-form">
        <div class="toolbar-filter-fields">
        <el-form-item label="教研组">
          <el-select
            v-model="filters.GroupId"
            clearable
            filterable
            placeholder="全部教研组"
            class="toolbar-field-w-lg"
            :disabled="!schoolId"
          >
            <el-option label="全部教研组" value="" />
            <el-option
              v-for="g in groupOptions"
              :key="g.value"
              :label="g.label"
              :value="g.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            size="small"
            :loading="loading"
            :disabled="!schoolId"
            icon="el-icon-search"
            @click="loadDashboard"
          >查询</el-button>
        </el-form-item>
        </div>
        <div class="toolbar-filter-actions">
          <el-button
            size="small"
            type="warning"
            :loading="exporting"
            :disabled="!schoolId || !canExport"
            icon="el-icon-download"
            @click="exportCoverage"
          >导出完成率</el-button>
        </div>
      </el-form>
    </div>

    <div v-if="!schoolId" class="ps-empty">
      <div class="ps-empty__icon"><i class="el-icon-office-building"></i></div>
      <p class="ps-empty__title">尚未选择学校</p>
      <p class="ps-empty__desc">请先在右上角切换到具体学校，再查看监管看板</p>
    </div>

    <div v-else v-loading="loading" class="ps-body">
      <!-- 流程漏斗 KPI -->
      <div class="ps-funnel">
        <div class="ps-section-head">
          <h2>流程漏斗</h2>
          <span>点击卡片弹窗查看该状态文档</span>
        </div>
        <div class="ps-funnel__grid">
          <button
            v-for="(item, idx) in primaryFunnel"
            :key="'f-' + item.Status"
            type="button"
            class="ps-kpi"
            :class="'ps-kpi--' + funnelTone(item.Status)"
            @click="openStatusDocs(item)"
          >
            <span class="ps-kpi__step">{{ idx + 1 }}</span>
            <span class="ps-kpi__label">{{ item.StatusText }}</span>
            <span class="ps-kpi__count">{{ item.Count }}</span>
            <i class="el-icon-arrow-right ps-kpi__go"></i>
          </button>
        </div>
        <div v-if="extraFunnel.length" class="ps-funnel__extra">
          <button
            v-for="item in extraFunnel"
            :key="'e-' + item.Status"
            type="button"
            class="ps-chip"
            @click="openStatusDocs(item)"
          >{{ item.StatusText }} · {{ item.Count }}</button>
        </div>
      </div>

      <!-- 列表区 -->
      <div class="ps-panel">
        <div class="ps-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="ps-tab"
            :class="{ 'is-active': activeTab === 'blocked' }"
            @click="activeTab = 'blocked'"
          >
            阻塞队列
            <em>{{ blocked.length }}</em>
          </button>
          <button
            type="button"
            role="tab"
            class="ps-tab"
            :class="{ 'is-active': activeTab === 'coverage' }"
            @click="activeTab = 'coverage'"
          >
            册/单元监管
            <em>{{ coverage.length }}</em>
          </button>
          <button
            type="button"
            role="tab"
            class="ps-tab"
            :class="{ 'is-active': activeTab === 'exceptions' }"
            @click="activeTab = 'exceptions'"
          >
            异常专区
            <em>{{ exceptions.length }}</em>
          </button>
        </div>

        <div class="ps-panel__body">
          <div class="ps-table-scroll">
          <el-table
            v-if="activeTab === 'blocked'"
            ref="blockedTable"
            :data="pagedBlocked"
            size="small"
            class="ps-table"
            height="100%"
            style="width: 100%"
            empty-text="当前没有阻塞项，流程运转正常"
          >
            <el-table-column label="类型" width="120">
              <template slot-scope="scope">
                <span class="ps-type" :class="'ps-type--' + (scope.row.BlockType || '')">
                  {{ blockTypeText(scope.row.BlockType) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="Title" label="教案" min-width="160" show-overflow-tooltip />
            <el-table-column prop="GroupName" label="教研组" width="120" show-overflow-tooltip />
            <el-table-column prop="WorkflowStatusText" label="流程" width="100" />
            <el-table-column prop="UploaderName" label="上传人" width="90" />
            <el-table-column prop="StuckHours" label="卡住(时)" width="80" align="center" />
            <el-table-column prop="BlockReason" label="说明" min-width="140" show-overflow-tooltip />
            <el-table-column label="操作" width="200" align="center">
              <template slot-scope="scope">
                <div class="table-op-pair">
                  <el-button type="text" class="table-op-link" @click="goDoc(scope.row.RootDocId)">详情</el-button>
                  <el-button
                    v-if="scope.row.BlockType === 'VoteStuck' && scope.row.CurrentVoteId"
                    type="text"
                    class="table-op-link"
                    @click="doCloseVote(scope.row)"
                  >代结票</el-button>
                  <el-button
                    v-if="scope.row.BlockType === 'DiscussionStuck' && scope.row.OpenDiscussionId"
                    type="text"
                    class="table-op-link"
                    @click="doEndDiscussion(scope.row)"
                  >代结束讨论</el-button>
                  <el-button
                    v-if="scope.row.BlockType === 'PassedNotPublished'"
                    type="text"
                    class="table-op-link"
                    @click="doPublish(scope.row)"
                  >代发布</el-button>
                  <el-button type="text" class="table-op-link" @click="doUrge(scope.row)">催办</el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <el-table
            v-else-if="activeTab === 'coverage'"
            :key="'coverage-' + coverageTableKey"
            ref="coverageTable"
            :data="pagedCoverage"
            size="small"
            class="ps-table ps-table--coverage"
            height="100%"
            style="width: 100%"
            empty-text="暂无册/单元数据，请先在教研组维护课本与目录"
          >
            <el-table-column prop="GroupName" label="教研组" width="120" />
            <el-table-column prop="TextbookTitle" label="课本册" min-width="140" />
            <el-table-column prop="UnitName" label="单元" min-width="110" />
            <el-table-column label="安排教师" min-width="180">
              <template slot-scope="scope">
                <span
                  v-if="coverageTeacherList(scope.row).length"
                  class="ps-teacher-link"
                  :title="coverageTeacherText(scope.row)"
                  @click="openCoverageTeachers(scope.row)"
                >
                  {{ coverageTeacherPreview(scope.row) }}
                  <template v-if="coverageTeacherList(scope.row).length > 2">
                    等{{ coverageTeacherList(scope.row).length }}人
                  </template>
                  <i class="el-icon-view"></i>
                </span>
                <span v-else class="text-muted">未安排</span>
              </template>
            </el-table-column>
            <el-table-column prop="LessonCount" label="应备" width="70" align="center" />
            <el-table-column prop="UploadedDocCount" label="已传" width="70" align="center" />
            <el-table-column label="完成率" width="90" align="center">
              <template slot-scope="scope">
                <span class="ps-rate">{{ coverageRate(scope.row) }}%</span>
              </template>
            </el-table-column>
            <el-table-column prop="StuckSummary" label="卡点" min-width="100" />
            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" :type="coverageTag(scope.row.CoverageStatus)">
                  {{ coverageStatusText(scope.row.CoverageStatus) }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>

          <el-table
            v-else
            ref="exceptionsTable"
            :data="pagedExceptions"
            size="small"
            class="ps-table"
            height="100%"
            style="width: 100%"
            empty-text="暂无异常项"
          >
            <el-table-column label="类型" width="130">
              <template slot-scope="scope">
                {{ scope.row.ExceptionType === 'LegacyNoLesson' ? '一期旧文档' : '目录残缺' }}
              </template>
            </el-table-column>
            <el-table-column prop="Title" label="名称" min-width="180" show-overflow-tooltip />
            <el-table-column prop="GroupName" label="教研组" width="120" />
            <el-table-column prop="Reason" label="说明" min-width="160" show-overflow-tooltip />
            <el-table-column label="操作" width="120" align="center">
              <template slot-scope="scope">
                <el-button
                  v-if="scope.row.RootDocId"
                  type="text"
                  size="small"
                  @click="goDoc(scope.row.RootDocId)"
                >去补绑</el-button>
                <span v-else class="text-muted">维护目录</span>
              </template>
            </el-table-column>
          </el-table>
          </div>

          <div class="ps-list-pager">
            <el-pagination
              @size-change="handleListSizeChange"
              @current-change="handleListPageChange"
              :current-page="listPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="listPageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="activeListTotal"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 漏斗状态文档弹窗（不跳转页面） -->
    <el-dialog
      :title="statusDialogTitle"
      :visible.sync="statusDialogVisible"
      width="1080px"
      top="8vh"
      append-to-body
      custom-class="ps-status-dialog"
      @closed="onStatusDialogClosed"
    >
      <el-table
        v-loading="statusDialogLoading"
        :data="statusDocList"
        size="small"
        class="ps-table"
        max-height="460"
        empty-text="该状态下暂无文档"
      >
        <el-table-column prop="Title" label="教案标题" min-width="150" show-overflow-tooltip />
        <el-table-column prop="GroupName" label="教研组" width="110" show-overflow-tooltip />
        <el-table-column prop="UploaderName" label="上传教师" width="90" />
        <el-table-column label="绑定课时" min-width="160">
          <template slot-scope="scope">
            <span
              v-if="(scope.row.BoundLessons && scope.row.BoundLessons.length) || (scope.row.LessonLabels && scope.row.LessonLabels.length)"
              class="ps-lesson-link"
              @click="openLessonLabelsDialog(scope.row)"
            >
              <span class="ps-lesson-link__text" :title="statusLessonPreview(scope.row)">{{ statusLessonPreview(scope.row) }}</span>
              <span v-if="statusLessonCount(scope.row) > 1"> 等 {{ statusLessonCount(scope.row) }} 个</span>
              <i class="el-icon-view"></i>
            </span>
            <el-tag v-else-if="scope.row.IsLegacyDoc" size="mini" type="info">一期旧文档</el-tag>
            <span v-else class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="流程" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="workflowTagType(scope.row.WorkflowStatus)">
              {{ scope.row.WorkflowStatusText || "—" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="2.0修订" width="90" align="center">
          <template slot-scope="scope">{{ scope.row.CurrentRevisionText || "无" }}</template>
        </el-table-column>
        <el-table-column label="投票进度" width="110" align="center">
          <template slot-scope="scope">{{ scope.row.VoteProgressText || "—" }}</template>
        </el-table-column>
        <el-table-column prop="UploadTime" label="上传时间" width="150" />
        <el-table-column label="操作" width="80" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="goDoc(scope.row.RootDocId || scope.row.Id)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="ps-dialog-pager">
        <el-pagination
          small
          layout="total, prev, pager, next"
          :current-page.sync="statusDocPage"
          :page-size="statusDocPageSize"
          :total="statusDocTotal"
          @current-change="loadStatusDocs"
        />
      </div>
      <div slot="footer">
        <el-button size="small" @click="statusDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 绑定课时完整列表 -->
    <el-dialog
      :visible.sync="lessonLabelsVisible"
      width="520px"
      append-to-body
      title="绑定课时"
    >
      <p class="ps-lesson-dialog__sub">
        {{ lessonLabelsDocTitle || "教案" }} · 共 {{ lessonLabelsList.length }} 个
      </p>
      <div v-if="lessonLabelsList.length" class="ps-lesson-dialog__list">
        <div v-for="(lb, idx) in lessonLabelsList" :key="idx" class="ps-lesson-dialog__item">
          <div class="ps-lesson-dialog__path">{{ lessonItemPath(lb) }}</div>
          <div v-if="lessonItemName(lb)" class="ps-lesson-dialog__name">课时名称：{{ lessonItemName(lb) }}</div>
          <div v-else class="ps-lesson-dialog__name is-empty">未填课时名称</div>
        </div>
      </div>
      <div v-else class="text-muted">暂无绑定课时</div>
      <div slot="footer">
        <el-button type="primary" size="small" @click="lessonLabelsVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 册/单元安排教师名单（标签换行，对齐教研组教师弹窗） -->
    <el-dialog
      :visible.sync="coverageTeacherVisible"
      width="480px"
      append-to-body
      custom-class="ps-teacher-dialog"
    >
      <div slot="title" class="ps-dialog-title">
        <span class="ps-dialog-title__mark"></span>
        <div>
          <div class="ps-dialog-title__main">安排教师</div>
          <div class="ps-dialog-title__sub">
            {{ coverageTeacherTitle }} · 共 {{ coverageTeacherNames.length }} 人
          </div>
        </div>
      </div>
      <div v-if="coverageTeacherNames.length" class="ps-teacher-grid">
        <span
          v-for="(name, idx) in coverageTeacherNames"
          :key="idx"
          class="ps-teacher-chip"
        >{{ name }}</span>
      </div>
      <div v-else class="text-muted">暂无安排教师</div>
      <div slot="footer">
        <el-button type="primary" size="small" @click="coverageTeacherVisible = false">关闭</el-button>
      </div>
    </el-dialog>
    </section>
  </section>
</template>

<script>
/**
 * 监管看板 UI：蓝橙数据看板风格（对齐会议室）。创建于 2026-07-26
 * 路由仍由数据库动态菜单加载。
 */
import { deptIdsEqual, normalizeDeptId } from "@/utils/deptCascaderProps";

export default {
  name: "PrepSupervise",
  data() {
    return {
      schoolId: "",
      loading: false,
      exporting: false,
      loadSeq: 0,
      activeTab: "blocked",
      canExport: false,
      funnel: [],
      blocked: [],
      coverage: [],
      coverageTableKey: 0,
      exceptions: [],
      listPage: 1,
      listPageSize: 10,
      groupOptions: [],
      filters: { GroupId: "" },
      statusDialogVisible: false,
      statusDialogLoading: false,
      statusDialogTitle: "状态文档",
      statusFilter: null,
      statusDocList: [],
      statusDocPage: 1,
      statusDocPageSize: 10,
      statusDocTotal: 0,
      lessonLabelsVisible: false,
      lessonLabelsDocTitle: "",
      lessonLabelsList: [],
      coverageTeacherVisible: false,
      coverageTeacherTitle: "",
      coverageTeacherNames: [],
      _api: null,
      _unwatchSchool: null
    };
  },
  computed: {
    schoolName() {
      return (this.$store && this.$store.state && this.$store.state.activeSchoolName) || "";
    },
    primaryFunnel() {
      const primary = [10, 30, 40, 60, 70];
      return (this.funnel || []).filter(x => primary.includes(x.Status));
    },
    extraFunnel() {
      const primary = [10, 30, 40, 60, 70];
      return (this.funnel || []).filter(x => !primary.includes(x.Status) && x.Count > 0);
    },
    activeListTotal() {
      if (this.activeTab === "blocked") return (this.blocked || []).length;
      if (this.activeTab === "coverage") return (this.coverage || []).length;
      return (this.exceptions || []).length;
    },
    pagedBlocked() {
      return this.slicePage(this.blocked);
    },
    pagedCoverage() {
      return this.slicePage(this.coverage);
    },
    pagedExceptions() {
      return this.slicePage(this.exceptions);
    }
  },
  watch: {
    activeTab() {
      this.listPage = 1;
      this.$nextTick(() => this.relayoutActiveTable());
    },
    listPage() {
      this.$nextTick(() => this.relayoutActiveTable());
    }
  },
  mounted() {
    this.$nextTick(() => {
      try {
        const routers = window.localStorage.router
          ? JSON.parse(window.localStorage.router)
          : [];
        const { getButtonList } = require("@/utils/routeButtonList");
        const buttons = getButtonList(this.$route.path, routers) || [];
        this.canExport = buttons.some(b => b && b.Func === "exportCoverage");
      } catch (e) {
        this.canExport = true;
      }

      this.bindSchool(this.$store.state.activeSchoolId);
      this._unwatchSchool = this.$store.watch(
        s => s.activeSchoolId,
        id => this.bindSchool(id)
      );
    });
  },
  beforeDestroy() {
    if (this._unwatchSchool) {
      this._unwatchSchool();
      this._unwatchSchool = null;
    }
  },
  methods: {
    /** 查看绑定课时完整列表 */
    openLessonLabelsDialog(row) {
      if (!row) return;
      this.lessonLabelsDocTitle = row.Title || row.title || "";
      const bound = row.BoundLessons || row.boundLessons || [];
      if (bound.length) {
        this.lessonLabelsList = bound.slice();
      } else {
        this.lessonLabelsList = (row.LessonLabels || row.lessonLabels || []).slice();
      }
      this.lessonLabelsVisible = true;
    },
    /** 课时路径（册/单元-类型-序号） */
    lessonItemPath(item) {
      if (item == null) return "—";
      if (typeof item === "string") {
        // 去掉末尾（课时名称）再展示路径
        return String(item).replace(/（[^）]*）\s*$/, "").replace(/·[^·]*$/, "") || item;
      }
      const book = item.TextbookTitle || item.textbookTitle || "";
      const unit = item.UnitName || item.unitName || "";
      const typeText = Number(item.LessonType || item.lessonType) === 2 ? "练习" : "新授";
      const no = item.LessonNo != null ? item.LessonNo : item.lessonNo;
      const base = `${unit}-${typeText}-${no}`;
      return book ? `${book}/${base}` : base;
    },
    /** 课时名称 */
    lessonItemName(item) {
      if (item == null) return "";
      if (typeof item === "string") {
        const m = String(item).match(/（([^）]+)）\s*$/);
        if (m) return m[1];
        const parts = String(item).split("·");
        return parts.length > 1 ? parts[parts.length - 1] : "";
      }
      return (item.LessonName || item.lessonName || "").trim();
    },
    statusLessonCount(row) {
      if (!row) return 0;
      const bound = row.BoundLessons || row.boundLessons || [];
      if (bound.length) return bound.length;
      return (row.LessonLabels || row.lessonLabels || []).length;
    },
    /** 列表预览：优先「路径 + 课时名称」 */
    statusLessonPreview(row) {
      if (!row) return "";
      const bound = row.BoundLessons || row.boundLessons || [];
      if (bound.length) {
        const first = bound[0];
        const path = this.lessonItemPath(first);
        const name = this.lessonItemName(first);
        return name ? `${path}（${name}）` : path;
      }
      const labels = row.LessonLabels || row.lessonLabels || [];
      return labels[0] || "";
    },
    funnelTone(status) {
      if (status === 10) return "warn";
      if (status === 30) return "process";
      if (status === 40) return "alert";
      if (status === 60) return "ready";
      if (status === 70) return "ok";
      return "muted";
    },
    /** 流程状态标签色，对齐备课文档列表 */
    workflowTagType(status) {
      if (status === 70) return "success";
      if (status === 60) return "";
      if (status === 50) return "danger";
      if (status === 40 || status === 30 || status === 35) return "warning";
      return "info";
    },
    slicePage(list) {
      const arr = list || [];
      const size = this.listPageSize > 0 ? this.listPageSize : 10;
      const page = this.listPage > 0 ? this.listPage : 1;
      const start = (page - 1) * size;
      return arr.slice(start, start + size);
    },
    handleListSizeChange(val) {
      this.listPageSize = val;
      this.listPage = 1;
    },
    handleListPageChange(val) {
      this.listPage = val;
    },
    async ensureApi() {
      if (this._api) return this._api;
      this._api = await import("@/api/modules/teaching-research");
      return this._api;
    },
    bindSchool(rawId) {
      const id = normalizeDeptId(rawId);
      if (deptIdsEqual(id, this.schoolId)) return;
      this.schoolId = id;
      this.filters.GroupId = "";
      this.funnel = [];
      this.blocked = [];
      this.coverage = [];
      this.exceptions = [];
      this.listPage = 1;
      this.groupOptions = [];
      if (!id) return;
      this.loadGroups();
      this.loadDashboard();
    },
    async loadGroups() {
      if (!this.schoolId) return;
      try {
        const api = await this.ensureApi();
        const res = await api.getAllGroups({ SchoolId: this.schoolId });
        if (!res.success) return;
        this.groupOptions = (res.response || []).map(g => ({
          label: g.GroupName,
          value: g.Id
        }));
      } catch (e) {
        /* ignore */
      }
    },
    async loadDashboard() {
      if (!this.schoolId) return;
      const seq = ++this.loadSeq;
      this.loading = true;
      try {
        const api = await this.ensureApi();
        const groupId =
          this.filters.GroupId !== "" && this.filters.GroupId != null
            ? Number(this.filters.GroupId)
            : null;
        const res = await api.getSuperviseDashboard({
          SchoolId: this.schoolId,
          GroupId: groupId > 0 ? groupId : null
        });
        if (seq !== this.loadSeq) return;
        if (!res.success) {
          this.$message.error(res.msg || "加载失败");
          return;
        }
        const d = res.response || {};
        this.funnel = d.Funnel || d.funnel || [];
        this.blocked = d.Blocked || d.blocked || [];
        this.coverage = (d.UnitCoverage || d.unitCoverage || []).map(x => this.normalizeCoverageRow(x));
        this.coverageTableKey += 1;
        this.exceptions = d.Exceptions || d.exceptions || [];
        this.listPage = 1;
        this.$nextTick(() => this.relayoutActiveTable());
      } catch (e) {
        if (seq === this.loadSeq) this.$message.error("加载监管看板失败");
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    /** 切换 Tab / 分页后重算表格列宽，避免课本册等列被挤成竖线 */
    relayoutActiveTable() {
      const run = () => {
        const refMap = {
          blocked: "blockedTable",
          coverage: "coverageTable",
          exceptions: "exceptionsTable"
        };
        const key = refMap[this.activeTab];
        const table = key && this.$refs[key];
        if (table && typeof table.doLayout === "function") {
          table.doLayout();
        }
      };
      run();
      // Element 在 v-if 刚挂载时偶发列宽为 0，延迟再算一次
      setTimeout(run, 60);
    },
    /** 册/单元行字段归一（兼容大小写） */
    normalizeCoverageRow(x) {
      const row = x || {};
      let namesRaw = row.AssignedTeacherNames || row.assignedTeacherNames || [];
      if (!Array.isArray(namesRaw) && namesRaw && typeof namesRaw === "object") {
        namesRaw = Object.keys(namesRaw)
          .filter(k => /^\d+$/.test(k))
          .sort((a, b) => Number(a) - Number(b))
          .map(k => namesRaw[k]);
      }
      let names = Array.isArray(namesRaw) ? namesRaw : [];
      if (!names.length && (row.AssignedTeachersText || row.assignedTeachersText)) {
        names = String(row.AssignedTeachersText || row.assignedTeachersText)
          .split(/[、,，]/)
          .map(s => s.trim())
          .filter(Boolean);
      }
      names = names.map(n => String(n || "").trim()).filter(Boolean);
      return {
        GroupId: row.GroupId != null ? row.GroupId : row.groupId,
        GroupName: row.GroupName || row.groupName || "",
        TextbookId: row.TextbookId != null ? row.TextbookId : row.textbookId,
        TextbookTitle: row.TextbookTitle || row.textbookTitle || "",
        UnitId: row.UnitId != null ? row.UnitId : row.unitId,
        UnitName: row.UnitName || row.unitName || "",
        AssignedTeacherNames: names,
        AssignedTeachersText: row.AssignedTeachersText || row.assignedTeachersText || names.join("、"),
        HasAssignment:
          row.HasAssignment != null
            ? row.HasAssignment
            : row.hasAssignment != null
              ? row.hasAssignment
              : names.length > 0,
        LessonCount: Number(row.LessonCount != null ? row.LessonCount : row.lessonCount) || 0,
        UploadedDocCount:
          Number(row.UploadedDocCount != null ? row.UploadedDocCount : row.uploadedDocCount) || 0,
        StuckSummary: row.StuckSummary || row.stuckSummary || "",
        CoverageStatus: row.CoverageStatus || row.coverageStatus || ""
      };
    },
    /** 读取覆盖行字段（兜底 camelCase） */
    coverageField(row, pascalKey) {
      if (!row || !pascalKey) return "";
      const camelKey = pascalKey.charAt(0).toLowerCase() + pascalKey.slice(1);
      const val = row[pascalKey] != null && row[pascalKey] !== "" ? row[pascalKey] : row[camelKey];
      return val == null ? "" : val;
    },
    /**
     * 点击漏斗：弹窗列出该状态文档，不跳转页面。
     */
    openStatusDocs(item) {
      if (!this.schoolId) {
        this.$message.warning("请先选择学校");
        return;
      }
      if (!item || item.Status == null) return;
      this.statusFilter = Number(item.Status);
      this.statusDialogTitle = `流程文档 · ${item.StatusText || "状态 " + item.Status}`;
      this.statusDocPage = 1;
      this.statusDialogVisible = true;
      this.loadStatusDocs();
    },
    async loadStatusDocs() {
      if (!this.schoolId || this.statusFilter == null) return;
      this.statusDialogLoading = true;
      try {
        const api = await this.ensureApi();
        const groupId =
          this.filters.GroupId !== "" && this.filters.GroupId != null
            ? Number(this.filters.GroupId)
            : null;
        const res = await api.getPrepDocumentPageForAdmin({
          SchoolId: this.schoolId,
          GroupId: groupId > 0 ? groupId : null,
          WorkflowStatus: this.statusFilter,
          OnlyVersion1: true,
          Page: this.statusDocPage,
          PageSize: this.statusDocPageSize
        });
        if (!res.success) {
          this.$message.error(res.msg || "加载文档失败");
          this.statusDocList = [];
          this.statusDocTotal = 0;
          return;
        }
        const page = res.response || {};
        this.statusDocList = (page.data || page.Data || []).map(x => ({
          ...x,
          Title: x.Title || x.title || "",
          GroupName: x.GroupName || x.groupName || "",
          LessonLabels: x.LessonLabels || x.lessonLabels || [],
          BoundLessons: x.BoundLessons || x.boundLessons || []
        }));
        this.statusDocTotal = page.dataCount != null ? page.dataCount : (page.DataCount || 0);
      } catch (e) {
        this.$message.error("加载文档失败");
        this.statusDocList = [];
        this.statusDocTotal = 0;
      } finally {
        this.statusDialogLoading = false;
      }
    },
    onStatusDialogClosed() {
      this.statusFilter = null;
      this.statusDocList = [];
      this.statusDocTotal = 0;
      this.statusDocPage = 1;
    },
    goDoc(rootDocId) {
      if (!rootDocId) return;
      this.statusDialogVisible = false;
      this.$router.push({
        path: "/TeachingResearch/PrepDocument",
        query: { OpenDocId: rootDocId, SchoolId: this.schoolId }
      });
    },
    blockTypeText(t) {
      return (
        {
          VoteStuck: "投票久未结",
          DiscussionStuck: "讨论卡住",
          PassedNotPublished: "通过未入库",
          UploaderLeft: "上传人离组"
        }[t] || t
      );
    },
    coverageRate(row) {
      const lessonCount = Number(this.coverageField(row, "LessonCount")) || 0;
      const uploaded = Number(this.coverageField(row, "UploadedDocCount")) || 0;
      if (!lessonCount) return 0;
      return Math.round((uploaded * 1000) / lessonCount) / 10;
    },
    /** 安排教师姓名列表（兼容数组 / 顿号字符串） */
    coverageTeacherList(row) {
      if (!row) return [];
      let list = row.AssignedTeacherNames || row.assignedTeacherNames || [];
      if (!Array.isArray(list)) {
        // Chrome/部分序列化可能把数组变成 {0:'张三',1:'李四'}
        if (list && typeof list === "object") {
          list = Object.keys(list)
            .filter(k => /^\d+$/.test(k))
            .sort((a, b) => Number(a) - Number(b))
            .map(k => list[k]);
        } else {
          list = [];
        }
      }
      if (!list.length) {
        const text = row.AssignedTeachersText || row.assignedTeachersText || "";
        if (text) list = String(text).split(/[、,，]/);
      }
      return list.map(x => String(x || "").trim()).filter(Boolean);
    },
    coverageTeacherText(row) {
      return this.coverageTeacherList(row).join("、");
    },
    coverageTeacherPreview(row) {
      return this.coverageTeacherList(row).slice(0, 2).join("、");
    },
    openCoverageTeachers(row) {
      const list = this.coverageTeacherList(row);
      if (!list.length) return;
      const groupName = this.coverageField(row, "GroupName");
      const unitName = this.coverageField(row, "UnitName") || "单元";
      this.coverageTeacherTitle = `${groupName} · ${unitName}`;
      this.coverageTeacherNames = list.slice();
      this.coverageTeacherVisible = true;
    },
    coverageStatusText(s) {
      return (
        {
          Unassigned: "未安排",
          NoLesson: "无课时",
          Pending: "待上传",
          InProgress: "进行中",
          Done: "已完成"
        }[s] || s
      );
    },
    coverageTag(s) {
      if (s === "Done") return "success";
      if (s === "Unassigned" || s === "NoLesson") return "danger";
      if (s === "Pending") return "info";
      return "warning";
    },
    async doCloseVote(row) {
      try {
        await this.$confirm("确认代结束投票并结算？", "提示", { type: "warning" });
        const api = await this.ensureApi();
        const res = await api.adminCloseVote({ VoteId: row.CurrentVoteId });
        if (res.success) {
          this.$message.success(res.msg || "已代结票");
          this.loadDashboard();
        } else this.$message.error(res.msg || "失败");
      } catch (e) {
        /* cancel */
      }
    },
    async doEndDiscussion(row) {
      try {
        await this.$confirm("确认代结束该讨论？", "提示", { type: "warning" });
        const api = await this.ensureApi();
        const res = await api.adminEndDiscussion({ DiscussionId: row.OpenDiscussionId });
        if (res.success) {
          this.$message.success(res.msg || "已结束讨论");
          this.loadDashboard();
        } else this.$message.error(res.msg || "失败");
      } catch (e) {
        /* cancel */
      }
    },
    async doPublish(row) {
      try {
        await this.$confirm("确认代发布到公共库？", "提示", { type: "info" });
        const api = await this.ensureApi();
        const res = await api.adminPublishToLibrary({ RootDocId: row.RootDocId });
        if (res.success) {
          this.$message.success(res.msg || "已代发布");
          this.loadDashboard();
        } else this.$message.error(res.msg || "失败");
      } catch (e) {
        /* cancel */
      }
    },
    async doUrge(row) {
      const map = {
        VoteStuck: "NeedVote",
        DiscussionStuck: "NeedV2",
        PassedNotPublished: "NeedPublish"
      };
      const api = await this.ensureApi();
      const res = await api.urgePrepDocument({
        SchoolId: this.schoolId,
        UrgeType: map[row.BlockType] || "NeedReview",
        RootDocId: row.RootDocId
      });
      if (res.success) this.$message.success(res.msg || "已催办");
      else this.$message.error(res.msg || "催办失败");
    },
    async exportCoverage() {
      if (!this.schoolId) return;
      try {
        const api = await this.ensureApi();
        const groupId =
          this.filters.GroupId !== "" && this.filters.GroupId != null
            ? Number(this.filters.GroupId)
            : null;
        const res = await api.exportUnitCoverageExcel({
          SchoolId: this.schoolId,
          GroupId: groupId > 0 ? groupId : null
        });
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `册单元完成率_${Date.now()}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$message.error("导出失败");
      }
    }
  }
};
</script>

<style scoped>
.ps-page {
  --ps-primary: #1e3a8a;
  --ps-secondary: #3b82f6;
  --ps-cta: #f97316;
  --ps-bg: #eff6ff;
  --ps-text: #0f172a;
  --ps-muted: #64748b;
  --ps-line: #dbeafe;
  --ps-card: #ffffff;
  color: var(--ps-text);
}

/* 与教研组等页一致：外层 dp-layout 留白，内层白卡片圆角边框 */
.ps-page.dp-layout {
  background-color: #f4f7fc;
}

.ps-shell {
  width: 100%;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background:
    radial-gradient(900px 220px at 0% 0%, rgba(59, 130, 246, 0.08), transparent 55%),
    #ffffff;
  border: 1px solid #eef2f7;
}

.ps-hero {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 14px;
  flex-shrink: 0;
}
.ps-eyebrow {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--ps-primary);
  text-transform: uppercase;
}
.ps-title {
  margin: 0;
  font-size: 24px;
  font-weight: 750;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.25;
}
.ps-sub {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--ps-muted);
  line-height: 1.55;
  white-space: nowrap;
}
.ps-hero__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}
.ps-school {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--ps-card);
  border: 1px solid var(--ps-line);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(30, 58, 138, 0.06);
}
.ps-school > i {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--ps-bg);
  color: var(--ps-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.ps-school > div {
  text-align: center;
}
.ps-school__k {
  display: block;
  font-size: 11px;
  color: var(--ps-muted);
  text-align: center;
}
.ps-school__v {
  display: block;
  font-size: 14px;
  color: #0f172a;
  text-align: center;
}
.ps-stat-mini {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: var(--ps-muted);
}
.ps-stat-mini b {
  color: var(--ps-primary);
  font-weight: 700;
}

.ps-filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  backdrop-filter: blur(6px);
  flex-shrink: 0;
}
.ps-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.ps-section-head h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
.ps-section-head span {
  font-size: 12px;
  color: var(--ps-muted);
}

.ps-funnel {
  margin-bottom: 16px;
  flex-shrink: 0;
}
.ps-body {
  flex: 1;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ps-funnel__grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  min-width: 0;
  gap: 8px;
}
.ps-kpi {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 14px 30px 14px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: var(--ps-card);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  overflow: hidden;
  min-height: 52px;
}
.ps-kpi::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #94a3b8;
}
.ps-kpi:hover {
  border-color: #bfdbfe;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.08);
  transform: translateY(-1px);
}
.ps-kpi__step {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  color: var(--ps-muted);
  margin: 0;
  line-height: 1;
}
.ps-kpi__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ps-kpi__count {
  flex: 0 0 auto;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  color: #0f172a;
  letter-spacing: -0.02em;
}
.ps-kpi__go {
  position: absolute;
  right: 10px;
  top: 50%;
  bottom: auto;
  margin-top: -6px;
  color: #cbd5e1;
  font-size: 12px;
  transition: color 0.2s ease, transform 0.2s ease;
}
.ps-kpi:hover .ps-kpi__go {
  color: var(--ps-primary);
  transform: translateX(2px);
}
.ps-kpi--warn::before { background: #f59e0b; }
.ps-kpi--process::before { background: #0ea5e9; }
.ps-kpi--alert::before { background: #ef4444; }
.ps-kpi--ready::before { background: #3b82f6; }
.ps-kpi--ok::before { background: #059669; }
.ps-kpi--warn .ps-kpi__count { color: #b45309; }
.ps-kpi--process .ps-kpi__count { color: #3b82f6; }
.ps-kpi--alert .ps-kpi__count { color: #b91c1c; }
.ps-kpi--ready .ps-kpi__count { color: #3b82f6; }
.ps-kpi--ok .ps-kpi__count { color: #059669; }

.ps-funnel__extra {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.ps-chip {
  border: 1px solid #bfdbfe;
  background: var(--ps-bg);
  color: var(--ps-primary);
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.2s ease;
}
.ps-chip:hover {
  background: #dbeafe;
}

.ps-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--ps-card);
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}
.ps-panel__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 4px 8px 12px;
}
.ps-table-scroll {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}
.ps-cell {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}
.ps-tabs {
  flex-shrink: 0;
  display: flex;
  gap: 0;
  padding: 0 8px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}
.ps-tab {
  position: relative;
  border: none;
  background: transparent;
  padding: 14px 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ps-muted);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s ease;
}
.ps-tab em {
  font-style: normal;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ps-tab:hover {
  color: var(--ps-primary);
}
.ps-tab.is-active {
  color: var(--ps-primary);
}
.ps-tab.is-active::after {
  content: "";
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 0;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: var(--ps-primary);
}
.ps-tab.is-active em {
  background: var(--ps-bg);
  color: var(--ps-primary);
}

.ps-table >>> .el-table__header th {
  background: #f8fafc !important;
  color: #475569;
  font-weight: 700;
}
.ps-table >>> .el-table__row:hover > td {
  background: #eff6ff !important;
}
.ps-table >>> .el-button--text {
  color: var(--ps-cta);
}
.ps-table {
  width: 100% !important;
}
.ps-table--coverage {
  width: 100% !important;
}
.ps-table--coverage >>> .el-table__body td .cell {
  line-height: 22px;
}
.ps-teacher-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
  padding: 4px 2px;
}
.ps-teacher-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  color: #1e3a8a;
  font-size: 13px;
  line-height: 1.4;
}
.ps-dialog-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding-right: 28px;
}
.ps-dialog-title__mark {
  width: 4px;
  height: 34px;
  margin-top: 2px;
  border-radius: 4px;
  background: linear-gradient(180deg, #1e3a8a, #3b82f6);
  flex-shrink: 0;
}
.ps-dialog-title__main {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}
.ps-dialog-title__sub {
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
}

.ps-type {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
}
.ps-type--VoteStuck {
  background: #fef2f2;
  color: #b91c1c;
}
.ps-type--DiscussionStuck {
  background: #fffbeb;
  color: #b45309;
}
.ps-type--PassedNotPublished {
  background: #eff6ff;
  color: #1d4ed8;
}
.ps-type--UploaderLeft {
  background: #faf5ff;
  color: #7e22ce;
}
.ps-rate {
  font-weight: 700;
  color: var(--ps-primary);
}

.ps-empty {
  margin-top: 40px;
  text-align: center;
  padding: 48px 20px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px dashed #bfdbfe;
  border-radius: 16px;
}
.ps-empty__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 16px;
  background: var(--ps-bg);
  color: var(--ps-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}
.ps-empty__title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}
.ps-empty__desc {
  margin: 0;
  font-size: 13px;
  color: var(--ps-muted);
}
.text-muted {
  color: #94a3b8;
  font-size: 12px;
}
.ps-teacher-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  color: #1e3a8a;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
}
.ps-teacher-link:hover {
  text-decoration: underline;
}
.ps-teacher-link__text {
  display: inline-block;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
.ps-teacher-tag {
  margin: 0 4px 2px 0;
}
.ps-teacher-tag.is-more {
  cursor: pointer;
}
.ps-lesson-dialog__path {
  font-size: 13px;
  color: #0f172a;
  font-weight: 600;
}
.ps-lesson-dialog__name {
  margin-top: 4px;
  font-size: 12px;
  color: #1e3a8a;
}
.ps-lesson-dialog__name.is-empty {
  color: #94a3b8;
}
.ps-lesson-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  color: #1e3a8a;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
}
.ps-lesson-link:hover {
  text-decoration: underline;
}
.ps-lesson-link__text {
  display: inline-block;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
.ps-lesson-dialog__sub {
  margin: 0 0 12px;
  font-size: 13px;
  color: #64748b;
}
.ps-lesson-dialog__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
}
.ps-lesson-dialog__item {
  padding: 8px 12px;
  border-radius: 8px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  color: #1e3a8a;
  font-size: 13px;
  line-height: 1.45;
  word-break: break-all;
}
.ps-dialog-pager,
.ps-list-pager {
  flex-shrink: 0;
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 1100px) {
  .ps-funnel__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 768px) {
  .ps-hero {
    flex-direction: column;
  }
  .ps-hero__meta {
    align-items: flex-start;
  }
  .ps-funnel__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .ps-shell,
  .ps-body,
  .ps-panel,
  .ps-panel__body,
  .ps-table-scroll {
    height: auto;
    flex: none;
    overflow: visible;
    min-height: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ps-kpi,
  .ps-kpi__go,
  .ps-chip,
  .ps-tab {
    transition: none;
  }
}
</style>
